/**
 * Workflow diagram with the current stage highlighted.
 */

import Box from "@cloudscape-design/components/box"
import Icon from "@cloudscape-design/components/icon"
import SpaceBetween from "@cloudscape-design/components/space-between"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import React from "react"
import type { DTADetails } from "../../services/dtaApi"
import { formatUserId } from "../../utils/userFormatter"

interface WorkflowDiagramProps {
  dtaId: string
  workflowStage?: string
  /**
   * Status history, used to show who activated or deactivated the DTA and when on the final stage
   * card. Omit to hide it.
   */
  statusHistory?: DTADetails["status_history"]
}

/**
 * A transition shown on the final stage card, e.g. "Activated by".
 */
interface TransitionEvent {
  /**
   * Target status, e.g. "ACTIVE". Also the list key.
   */
  toStatus: string
  label: string
  changedBy: string
  changedAt: string
}

const STAGE_ORDER = ["DRAFT", "REVIEW", "APPROVED", "ACTIVE"] as const

/**
 * Workflow completion percentage for a stage (0-100). Exported for the Workflow header badge.
 */
export function getWorkflowProgress(workflowStage?: string): number {
  const currentStage = workflowStage || "DRAFT"
  if (currentStage === "INACTIVE" || currentStage === "INVALID") return 100
  const idx = STAGE_ORDER.indexOf(currentStage as (typeof STAGE_ORDER)[number])
  return idx >= 0 ? Math.round(((idx + 1) / STAGE_ORDER.length) * 100) : 0
}

const WorkflowDiagram: React.FC<WorkflowDiagramProps> = ({
  dtaId: _dtaId,
  workflowStage,
  statusHistory,
}) => {
  const currentStage = workflowStage || "DRAFT"
  const currentIdx = STAGE_ORDER.indexOf(currentStage as (typeof STAGE_ORDER)[number])
  const isInactive = currentStage === "INACTIVE"
  const isInvalid = currentStage === "INVALID"
  const lastIndex = STAGE_ORDER.length - 1

  // The backend returns status_history newest-first, so the first match is the latest event.
  const findTransition = (toStatus: string) => statusHistory?.find((h) => h.to_status === toStatus)
  const activation = findTransition("ACTIVE")
  const deactivation = findTransition("INACTIVE")
  const finalStepEvents: TransitionEvent[] = []
  if (activation) {
    finalStepEvents.push({
      toStatus: "ACTIVE",
      label: "Activated by",
      changedBy: activation.changed_by,
      changedAt: activation.changed_at,
    })
  }
  if (deactivation) {
    finalStepEvents.push({
      toStatus: "INACTIVE",
      label: "Deactivated by",
      changedBy: deactivation.changed_by,
      changedAt: deactivation.changed_at,
    })
  }

  const getStageStatus = (index: number): string => {
    if (isInactive) return index === lastIndex ? "DEACTIVATED" : "COMPLETED"
    if (isInvalid) return "COMPLETED"
    if (index < currentIdx) return "COMPLETED"
    if (index === currentIdx) return currentIdx === lastIndex ? "COMPLETED" : "CURRENT"
    return "PENDING"
  }

  const getStageDisplayName = (stage: string) => {
    const displayNames: Record<string, string> = {
      DRAFT: "Draft",
      REVIEW: "Review",
      APPROVED: "Approved",
      ACTIVE: "Active",
      INACTIVE: "Inactive",
      INVALID: "Invalid",
    }
    return displayNames[stage] || stage.replace("_", " ")
  }

  return (
    <Box padding="l">
      {isInactive && (
        <Box margin={{ bottom: "s" }}>
          <StatusIndicator type="stopped">
            This DTA has been deactivated and is no longer accepting data.
          </StatusIndicator>
        </Box>
      )}
      {isInvalid && (
        <Box margin={{ bottom: "s" }}>
          <StatusIndicator type="error">This DTA version has been invalidated.</StatusIndicator>
        </Box>
      )}

      {/* Inline-styled because Cloudscape has no stepper primitive. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        {STAGE_ORDER.map((stage, index) => {
          const stageStatus = getStageStatus(index)
          const isLast = index === lastIndex
          // Show INACTIVE instead of ACTIVE on the final step once deactivated.
          const effectiveStage = isInactive && isLast ? "INACTIVE" : stage
          return (
            <React.Fragment key={stage}>
              <StageCard
                effectiveStage={effectiveStage}
                stageStatus={stageStatus}
                displayName={getStageDisplayName(effectiveStage)}
                transitionEvents={isLast ? finalStepEvents : undefined}
              />
              {index < STAGE_ORDER.length - 1 && (
                <StageConnector completed={stageStatus === "COMPLETED"} />
              )}
            </React.Fragment>
          )
        })}
      </div>
    </Box>
  )
}

interface StageCardProps {
  /**
   * Stage the card shows, already resolved by the parent (e.g. INACTIVE for a deactivated final
   * step).
   */
  effectiveStage: string
  stageStatus: string
  displayName: string
  transitionEvents?: TransitionEvent[]
}

// Colors match Cloudscape's status palette, since Cloudscape has no step-card component.
const STAGE_CARD_STYLES: Record<string, React.CSSProperties> = {
  COMPLETED: {
    border: "2px solid #037f0c",
    borderRadius: "8px",
    padding: "12px",
    textAlign: "center",
    flex: 1,
    minWidth: "120px",
    backgroundColor: "#f2fcf3",
  },
  // Current stage: white text on Cloudscape info blue (#0972d3) so it stands out.
  CURRENT: {
    border: "2px solid #0972d3",
    borderRadius: "8px",
    padding: "12px",
    textAlign: "center",
    flex: 1,
    minWidth: "120px",
    backgroundColor: "#0972d3",
    color: "#ffffff",
    boxShadow: "0 0 0 3px rgba(9, 114, 211, 0.2)",
  },
  PENDING: {
    border: "2px solid #8d9096",
    borderRadius: "8px",
    padding: "12px",
    textAlign: "center",
    flex: 1,
    minWidth: "120px",
    backgroundColor: "#fafafa",
  },
  // Deactivated final step: Cloudscape stopped grey (#5f6b7a) so it reads as halted, not pending.
  DEACTIVATED: {
    border: "2px solid #5f6b7a",
    borderRadius: "8px",
    padding: "12px",
    textAlign: "center",
    flex: 1,
    minWidth: "120px",
    backgroundColor: "#f4f4f4",
  },
}

const StageCard: React.FC<StageCardProps> = ({
  effectiveStage,
  stageStatus,
  displayName,
  transitionEvents = [],
}) => {
  const cardStyle = STAGE_CARD_STYLES[stageStatus] || STAGE_CARD_STYLES.PENDING
  const isCurrent = stageStatus === "CURRENT"
  const isDeactivated = stageStatus === "DEACTIVATED"

  return (
    <div
      style={cardStyle}
      title={displayName}
      data-current={isCurrent}
      data-testid={`stage-card-${effectiveStage}`}
    >
      <Box margin={{ bottom: "xxs" }}>
        {stageStatus === "COMPLETED" && (
          <StatusIndicator type="success" iconAriaLabel="Completed">
            {" "}
          </StatusIndicator>
        )}
        {isCurrent && (
          <span aria-label="Current" role="img">
            <Icon name="status-in-progress" variant="inverted" />
          </span>
        )}
        {stageStatus === "PENDING" && (
          <StatusIndicator type="pending" iconAriaLabel="Pending">
            {" "}
          </StatusIndicator>
        )}
        {isDeactivated && (
          <StatusIndicator type="stopped" iconAriaLabel="Deactivated">
            {" "}
          </StatusIndicator>
        )}
      </Box>
      {isCurrent ? (
        <strong style={{ color: "#ffffff" }}>{displayName}</strong>
      ) : (
        <Box variant="strong">{displayName}</Box>
      )}

      {transitionEvents.length > 0 && (
        <Box margin={{ top: "xs" }}>
          <SpaceBetween size="xxxs">
            {transitionEvents.map((event) => (
              <Box key={event.toStatus} variant="small" color="text-body-secondary">
                {event.label} {formatUserId(event.changedBy)} •{" "}
                {new Date(event.changedAt).toLocaleDateString()}
              </Box>
            ))}
          </SpaceBetween>
        </Box>
      )}
    </div>
  )
}

/**
 * Arrow between stage cards, inline-styled because Cloudscape has no connector primitive.
 */
const StageConnector: React.FC<{ completed: boolean }> = ({ completed }) => {
  const color = completed ? "#037f0c" : "#8d9096"
  return (
    <div
      aria-hidden="true"
      style={{
        flexShrink: 0,
        width: "32px",
        height: "2px",
        backgroundColor: color,
        position: "relative",
        margin: "0 4px",
      }}
    >
      <div
        style={{
          position: "absolute",
          right: "-6px",
          top: "-4px",
          width: 0,
          height: 0,
          borderLeft: `8px solid ${color}`,
          borderTop: "5px solid transparent",
          borderBottom: "5px solid transparent",
        }}
      />
    </div>
  )
}

export default WorkflowDiagram
